import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import parking from 'src/assets/images/parking.svg';
import { useGetMembership } from 'src/features/membership/api/useGetMembership';
import { useListMembershipZoneRules } from 'src/features/membership/api/zoneRule/useListMembershipZoneRules';
import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { membershipMessages, identifiersMessages } from 'src/features/membership/utils/messages';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import {
    DURATION_UNIT_PLURAL_TRANSLATION_MAP,
    membershipTypeBadgeOptions,
} from 'src/features/membershipType/utils/constants';
import { PATH } from 'src/features/navigation/utils/constants';
import { PERIOD_TYPE } from 'src/features/stay/types';
import { Translation } from 'src/types';
import { GeneralBlock, GeneralBlockValue } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { useTitleStore } from 'src/stores/useTitleStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { ENTITY_TYPE } from 'src/features/entity/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import MembershipDashboardTables from 'src/features/membership/components/MembershipDashboardTables';
import BackButton from 'src/features/navigation/components/BackButton';
import { membershipStatusBadgeOptions } from 'src/features/membership/utils/constants';
import MembershipActionButtons from 'src/features/membership/components/MembershipActionButtons';
import { getNormalizedDuration } from 'src/features/membership/utils/getNormalizedDuration';
import MembershipConfiguration from 'src/features/membership/components/MembershipConfiguration';

dayjs.extend(duration);

const formatTime = (date: string) => date.split('.')[0];

export default function MembershipDashboard() {
    const { membershipId = '' } = useParams();
    const { formatMessage, locale } = useIntl();
    const manageMembershipsPermission = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const accessEntityPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessMembershipTemplatePermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    const { parkingId } = useParking();
    const { data, isLoading } = useGetMembership({ parkingId, membershipId });

    const { data: membershipTypeData } = useGetMembershipType({
        parkingId,
        membershipTypeId: data?.membership_type.id || '',
        // Read the template fresh on every visit so config-driven UI (e.g. the "create zone rule"
        // button, gated by allow_custom_space_count_definition) reflects the latest template edit,
        // not a value cached up to staleTime (10 min). refetchOnWindowFocus is off in this app.
        config: { enabled: !!data?.membership_type.id, staleTime: 0, refetchOnMount: 'always' },
    });

    // Surface legacy/invalid zone-rule config: when the template requires a custom allowed-spaces
    // count (both flags on) but a STAY rule on this membership still has a null count (e.g. a record
    // created before that field became required)
    const { data: membershipZoneRules } = useListMembershipZoneRules({
        parkingId,
        membershipId,
        config: { enabled: !!membershipId, staleTime: 0, refetchOnMount: 'always' },
    });
    const hasInvalidZoneRuleConfig =
        !!membershipTypeData?.has_allowed_spaces_count &&
        !!membershipTypeData?.allow_custom_space_count_definition &&
        (membershipZoneRules?.results ?? []).some(
            (rule) =>
                rule.allowed_stay_type === PERIOD_TYPE.STAY &&
                (rule.allowed_spaces_count === null || rule.allowed_spaces_count === undefined),
        );
    const { setTitle, removeTitle } = useTitleStore();

    const { duration, customDuration } = React.useMemo(() => {
        const result = {
            duration: '-',
            customDuration: false,
        };
        const validTo = data?.valid_to;
        const validFrom = data?.valid_from;
        const hasTypeDuration = membershipTypeData?.duration && membershipTypeData?.duration_unit;

        if (!validTo && !hasTypeDuration) return result;

        if (hasTypeDuration) {
            result.duration = formatMessage(DURATION_UNIT_PLURAL_TRANSLATION_MAP[membershipTypeData?.duration_unit], {
                value: membershipTypeData?.duration,
            });
        }

        if (!validTo) return result;

        const membershipTypeDuration =
            hasTypeDuration && membershipTypeData
                ? dayjs.duration(membershipTypeData.duration!, membershipTypeData?.duration_unit).asMilliseconds()
                : 0;
        const from = dayjs(validFrom);
        const to = dayjs(validTo);
        const membershipDuration = to.diff(from);
        result.customDuration = membershipDuration !== membershipTypeDuration;

        const durationsArray = getNormalizedDuration(membershipDuration);
        result.duration = durationsArray
            .map(({ unit, value }) => formatMessage(DURATION_UNIT_PLURAL_TRANSLATION_MAP[unit], { value }))
            .join(' ');
        return result;
    }, [data?.valid_from, data?.valid_to, formatMessage, membershipTypeData]);

    const categoryBadgeOption = React.useMemo(
        () => (data ? membershipTypeBadgeOptions[data?.membership_type.category] : null),
        [data],
    );

    const templateId = data?.membership_type?.id ?? '';

    React.useEffect(() => {
        setTitle(formatMessage(membershipMessages.membershipDashboard));

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const translationStatus =
        data?.status && membershipStatusBadgeOptions[data?.status]?.label
            ? membershipStatusBadgeOptions[data?.status]?.label
            : undefined;

    return (
        <>
            <BackButton customRoute={`${PATH.MEMBERSHIPS}`} />
            <div className="flex flex-row items-end justify-between p-0">
                <span className="flex flex-row items-center">
                    <SectionBlockTitle className="mr-2">{data?.name}</SectionBlockTitle>
                    {categoryBadgeOption && (
                        <Badge theme={categoryBadgeOption.theme}>
                            <FontAwesomeIcon icon={categoryBadgeOption.icon!} />
                            {formatMessage(categoryBadgeOption.label)}
                        </Badge>
                    )}
                </span>
                {data && manageMembershipsPermission && <MembershipActionButtons data={data} />}
            </div>

            {data && <MembershipConfiguration membership={data} />}

            <div className="flex flex-col gap-9">
                <div className="grid grid-cols-1 gap-9 md:flex">
                    <GeneralBlock className="flex-1 items-center justify-center">
                        <img src={parking} alt="Parking" />
                    </GeneralBlock>
                    <GeneralBlock className="grid flex-3 grid-cols-3">
                        <div className="flex flex-col justify-between">
                            {isLoading ? (
                                <Skeleton className="size-14 rounded-full" />
                            ) : (
                                <div
                                    className={cn(
                                        'flex size-14 items-center justify-center rounded-full border-8 p-1',
                                        data?.in_parking
                                            ? 'border-green-50 bg-green-100 text-green-700'
                                            : 'border-red-50 bg-red-100 text-red-700',
                                    )}
                                >
                                    <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} className="text-2xl" />
                                </div>
                            )}
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.identifierCount, {
                                    count: data?.membership_identifiers_count,
                                })}
                            >
                                <p className="text-xl font-semibold lg:text-3xl">
                                    <FormattedMessage
                                        {...(data?.in_parking
                                            ? membershipMessages.inParking
                                            : membershipMessages.notInParking)}
                                    />
                                </p>
                            </GeneralBlockAttribute>
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.owner)}
                                value={data?.owner?.name}
                                link={
                                    accessEntityPermission && data?.owner?.id
                                        ? `/${PATH.ENTITIES}/${ENTITY_TYPE.PERSON}/${data.owner.id}`
                                        : undefined
                                }
                                icon={ICON_MAP.ARROW_UP_RIGHT}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.maxAllowedSpaces)}
                                value={data?.max_allowed_spaces_count}
                                titleClassName={'break-words whitespace-normal'}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.maxExtraVisitors)}
                                value={membershipTypeData?.max_extra_visitors_count}
                                titleClassName={'break-words whitespace-normal'}
                            />
                            <GeneralBlockAttribute label={formatMessage(membershipMessages.allowedDays)}>
                                <GeneralBlockValue size="small" className="flex">
                                    {data && data.membership_allowed_times.length
                                        ? Object.values(DAY_OF_WEEK).map((day: DAY_OF_WEEK) => {
                                              const allowedTime = data.membership_allowed_times.find(
                                                  (time) => time.day_of_week === day,
                                              );
                                              const weekDay = formatMessage(membershipMessages[day]);
                                              return (
                                                  <TooltipProvider key={day}>
                                                      <Tooltip>
                                                          <TooltipTrigger asChild>
                                                              <Badge
                                                                  variant="tag"
                                                                  className={cn(
                                                                      'mr-1 flex size-6 shrink-0 items-center justify-center bg-white px-0 uppercase opacity-30 last:mr-0',
                                                                      allowedTime?.day_of_week === day &&
                                                                          'font-semibold opacity-100',
                                                                  )}
                                                                  key={day}
                                                              >
                                                                  {weekDay.charAt(0)}
                                                              </Badge>
                                                          </TooltipTrigger>
                                                          <TooltipContent side="bottom" className="flex flex-col gap-2">
                                                              <span className="text-gray-500">{weekDay}</span>
                                                              {allowedTime
                                                                  ? `${formatTime(allowedTime.from_time)} - ${formatTime(allowedTime.to_time)}`
                                                                  : formatMessage(membershipMessages.notAllowed)}
                                                          </TooltipContent>
                                                      </Tooltip>
                                                  </TooltipProvider>
                                              );
                                          })
                                        : '-'}
                                </GeneralBlockValue>
                            </GeneralBlockAttribute>
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.validFrom)}
                                value={getFormattedDateTimeForDetailPanel(data?.valid_from)}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(membershipMessages.validTo)}
                                value={getFormattedDateTimeForDetailPanel(data?.valid_to)}
                            />
                            <GeneralBlockAttribute label={formatMessage(membershipMessages.status)}>
                                {data?.status && (
                                    <Badge theme={membershipStatusBadgeOptions[data?.status]?.theme ?? 'gray'}>
                                        {translationStatus ? formatMessage(translationStatus) : data?.status}
                                    </Badge>
                                )}
                            </GeneralBlockAttribute>
                        </div>
                    </GeneralBlock>
                </div>
                <div className="grid grid-cols-1 gap-9 md:flex">
                    <GeneralBlock className="flex max-h-max flex-1 flex-col">
                        {hasInvalidZoneRuleConfig && (
                            <p className="text-sm font-medium text-red-500">
                                <FormattedMessage {...identifiersMessages.invalidConfiguration} />
                            </p>
                        )}
                        <GeneralBlockAttribute label={formatMessage(commonMessages.refNumber)} value={data?.ref} />
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.template)}>
                            {data ? (
                                <GeneralBlockValue size="small">
                                    <LinkWithIcon
                                        to={`/${PATH.ADMINISTRATION}/membership-templates/${data?.membership_type.id}`}
                                        linkTitle={translate(data?.membership_type.translations)}
                                        hasPermissions={accessMembershipTemplatePermission}
                                    />
                                </GeneralBlockValue>
                            ) : (
                                '-'
                            )}
                            {categoryBadgeOption && (
                                <Badge theme={categoryBadgeOption.theme}>
                                    <FontAwesomeIcon icon={categoryBadgeOption.icon!} />
                                    {formatMessage(categoryBadgeOption.label)}
                                </Badge>
                            )}
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.pricedPerSpace)}>
                            <BooleanBadge booleanField={data?.membership_type.apply_pricing_per_space} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.autoRenew)}>
                            <BooleanBadge booleanField={data?.membership_type.auto_renew} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.limitDuration)}>
                            <BooleanBadge booleanField={data?.membership_type.limit_duration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.customDuration)}>
                            <BooleanBadge booleanField={customDuration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.duration)} value={duration} />
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.limitDuration)}>
                            <BooleanBadge booleanField={data?.membership_type.limit_duration} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute label={formatMessage(membershipMessages.accessOverCapacityLimit)}>
                            <BooleanBadge booleanField={data?.membership_type.allow_access_over_capacity_limit} />
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute
                            label={formatMessage(membershipMessages.accessClientOverRestriction)}
                            titleClassName="whitespace-normal"
                        >
                            <BooleanBadge
                                booleanField={data?.membership_type.allow_access_as_client_outside_restrictions}
                            />
                        </GeneralBlockAttribute>
                    </GeneralBlock>

                    <MembershipDashboardTables membershipTypeData={membershipTypeData} templateId={templateId} />
                </div>
            </div>
        </>
    );
}
